export default (propertyMap = {}, searchPropertyMap = {}, useParent = true) => ({
    showMenu: false,
    activeStep: 'select-filter', // 'select-filter' | 'configure-filter' | 'ai-filter'
    selectedFilter: null,
    selectedValues: [],
    searchQuery: '',
    filterOptions: [],
    groupedOptions: null, // For grouped filter options (e.g., date presets)
    loading: false,
    hasMorePages: false,
    currentPage: 1,
    autoApplyTimeout: null, // For debouncing auto-apply
    aiFilterQuery: '', // For AI filter input
    filterMenuSearch: '', // Client-side search for the add-filter list
    highlightedIndex: 0, // Keyboard-highlighted filter in the add-filter list
    useParent: useParent, // True on the dashboard bar, false in a comparison editor.
                          // Only picks which property holds the staged filters.
    integerValue: null, // For integer filter input
    errorMessage: null, // Error message from server

    // Score filter state
    scoreFilterQuestion: null,
    scoreFilterOperator: 'gte',
    scoreFilterValue: null,
    scoreFilterMin: null,
    scoreFilterMax: null,
    scoreFilterOperators: [],

    init() {
        this.$watch('showMenu', (value) => {
            if (!value) {
                this.resetSelection();
            }
        });

        this.$watch('selectedFilter', async (filter) => {
            if (filter) {
                this.selectedValues = [];

                if (filter.filterType === 'score_filter') {
                    this.resetScoreFilterState();
                    this.loadScoreFilterOperators();
                } else {
                    // Start from what is already staged for this filter, so
                    // reopening it shows the current picks ticked rather than a
                    // blank list that looks like nothing was ever chosen.
                    await this.loadCurrentValues(filter);
                }

                if (filter.requiresOptions) {
                    this.loadFilterOptions(filter);
                }
            }
        });

        this.$watch('searchQuery', (query) => {
            this.filterSearchResults(query);
        });

        this.$watch('filterMenuSearch', () => {
            this.highlightedIndex = 0;
        });
    },

    get filteredOptions() {
        if (!this.searchQuery || !this.filterOptions.length) {
            return this.filterOptions;
        }

        return this.filterOptions.filter(option => {
            const label = option.dropdown_label || option.name;

            return label && label.toLowerCase().includes(this.searchQuery.toLowerCase());
        });
    },

    get visibleFilterGroups() {
        const groups = this.$wire.get('availableFilters') || [];
        const query = this.filterMenuSearch.trim().toLowerCase();

        let flatIndex = 0;

        return groups
            .map(group => group.filter(filter => !query || (filter.name && filter.name.toLowerCase().includes(query))))
            .filter(group => group.length > 0)
            .map(group => group.map(filter => ({ ...filter, flatIndex: flatIndex++ })));
    },

    get visibleFilters() {
        return this.visibleFilterGroups.flat();
    },

    highlightFilter(direction) {
        const count = this.visibleFilters.length;

        if (count === 0) {
            return;
        }

        this.highlightedIndex = (this.highlightedIndex + direction + count) % count;
        this.scrollHighlightedIntoView();
    },

    selectHighlightedFilter() {
        const filter = this.visibleFilters[this.highlightedIndex];

        if (filter) {
            this.selectFilter(filter);
        }
    },

    scrollHighlightedIntoView() {
        this.$nextTick(() => {
            const item = this.$refs.filterList?.querySelector(`[data-filter-index="${this.highlightedIndex}"]`);
            item?.scrollIntoView({ block: 'nearest' });
        });
    },

    get canApplyFilter() {
        return this.selectedFilter && this.selectedValues.length > 0;
    },

    // Whether the currently selected operator needs a min/max range rather than a
    // single value. Driven by the operator's `requiresRange` flag from the backend,
    // falling back to 'between' while the operator list is still loading.
    get scoreFilterRequiresRange() {
        const operator = this.scoreFilterOperators.find(op => op.value === this.scoreFilterOperator);

        return operator ? operator.requiresRange : this.scoreFilterOperator === 'between';
    },

    get scoreFilterRangeInvalid() {
        if (!this.scoreFilterRequiresRange) {
            return false;
        }

        const min = parseFloat(this.scoreFilterMin);
        const max = parseFloat(this.scoreFilterMax);

        return !isNaN(min) && !isNaN(max) && min > max;
    },

    get canApplyScoreFilter() {
        if (!this.scoreFilterQuestion) {
            return false;
        }

        if (this.scoreFilterRequiresRange) {
            return this.scoreFilterMin !== null && this.scoreFilterMax !== null &&
                   this.scoreFilterMin !== '' && this.scoreFilterMax !== '' &&
                   !this.scoreFilterRangeInvalid;
        }

        return this.scoreFilterValue !== null && this.scoreFilterValue !== '';
    },

    get filterValueLabel() {
        if (!this.selectedFilter || this.selectedValues.length === 0) {
            return '';
        }

        if (this.selectedFilter.filterType === 'boolean') {
            return this.selectedValues[0] ? 'Yes' : 'No';
        }


        if (this.selectedFilter.filterType === 'multiselect') {
            if (this.selectedValues.length === 1) {
                const option = this.filterOptions.find(opt => opt.id === this.selectedValues[0]);
                return option ? option.name : this.selectedValues[0];
            }
            return `${this.selectedValues.length} selected`;
        }

        if (this.selectedFilter.filterType === 'date_preset') {
            const option = this.filterOptions.find(opt => opt.value === this.selectedValues[0]);
            return option ? option.label : this.selectedValues[0];
        }

        if (this.selectedFilter.filterType === 'year_period') {
            const option = this.filterOptions.find(opt => opt.value === this.selectedValues[0]);
            return option ? option.label : this.selectedValues[0];
        }

        return this.selectedValues.join(', ');
    },

    resetSelection() {
        this.activeStep = 'select-filter';
        this.selectedFilter = null;
        this.selectedValues = [];
        this.searchQuery = '';
        this.filterMenuSearch = '';
        this.highlightedIndex = 0;
        this.filterOptions = [];
        this.groupedOptions = null;
        this.loading = false;
        this.hasMorePages = false;
        this.currentPage = 1;
        this.aiFilterQuery = '';
        this.integerValue = null;
        this.errorMessage = null;
        this.resetScoreFilterState();

        // Clear any pending auto-apply timeout
        if (this.autoApplyTimeout) {
            clearTimeout(this.autoApplyTimeout);
            this.autoApplyTimeout = null;
        }
    },

    resetScoreFilterState() {
        this.scoreFilterQuestion = null;
        this.scoreFilterOperator = 'gte';
        this.scoreFilterValue = null;
        this.scoreFilterMin = null;
        this.scoreFilterMax = null;
    },

    async loadScoreFilterOperators() {
        try {
            this.scoreFilterOperators = await this.$wire.call('getScoreFilterOperators');
        } catch (error) {
            console.error('Failed to load score filter operators:', error);
            this.scoreFilterOperators = [];
        }
    },

    selectScoreQuestion(question) {
        this.scoreFilterQuestion = question;
    },

    applyScoreFilter() {
        if (!this.canApplyScoreFilter) {
            return;
        }

        const scoreFilter = {
            question_id: this.scoreFilterQuestion.id,
            operator: this.scoreFilterOperator,
        };

        if (this.scoreFilterRequiresRange) {
            scoreFilter.min = parseFloat(this.scoreFilterMin);
            scoreFilter.max = parseFloat(this.scoreFilterMax);
        } else {
            scoreFilter.value = parseFloat(this.scoreFilterValue);
        }

        // Get existing score filters and add the new one
        const property = this.getFilterProperty(this.selectedFilter);
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;

        // Score conditions accumulate on whichever set the target stages into:
        // the dashboard's pending filters, or the comparison editor's own.
        const currentFilters = target.get(this.stagedFilterProperty('scoreFilters')) || [];
        const updatedFilters = [...currentFilters, scoreFilter];

        target.updateFilter(property, updatedFilters);
        this.closeFilterMenu();
    },

    handleEscape() {
        // Score filter: step back from the operator/value step to question selection first.
        if (this.activeStep === 'configure-filter' && this.selectedFilter?.filterType === 'score_filter' && this.scoreFilterQuestion) {
            this.scoreFilterQuestion = null;
            return;
        }

        // Configure / AI steps step back to the filter list; the list closes the menu.
        if (this.activeStep === 'select-filter') {
            this.closeFilterMenu();
            return;
        }

        this.backToFilterSelection();
    },

    openFilterMenu() {
        this.showMenu = true;
        this.$nextTick(() => {
            if (this.$refs.filterSearch) {
                this.$refs.filterSearch.focus();
            }
        });
    },

    closeFilterMenu() {
        this.showMenu = false;
    },

    selectFilter(filter) {
        this.selectedFilter = filter;
        this.activeStep = 'configure-filter';
        this.$nextTick(() => {
            if ((filter.filterType === 'multiselect' || filter.filterType === 'score_filter') && this.$refs.valueSearch) {
                this.$refs.valueSearch.focus();
            }
        });
    },

    backToFilterSelection() {
        this.activeStep = 'select-filter';
        this.selectedFilter = null;
        this.$nextTick(() => {
            if (this.$refs.filterSearch) {
                this.$refs.filterSearch.focus();
            }
        });
    },

    selectAiFilter() {
        this.activeStep = 'ai-filter';
        this.$nextTick(() => {
            if (this.$refs.aiFilterInput) {
                this.$refs.aiFilterInput.focus();
            }
        });
    },

    async applyAiFilter() {
        if (!this.aiFilterQuery.trim()) {
            return;
        }

        this.loading = true;
        this.errorMessage = null;

        try {
            // A message means part of the request could not be used, so the
            // panel stays open to show it rather than closing on a silent miss.
            const message = await this.$wire.applyAiFilters(this.aiFilterQuery.trim());

            if (message) {
                this.errorMessage = message;
            } else {
                this.closeFilterMenu();
            }
        } catch (error) {
            console.error('Failed to apply AI filters:', error);
            this.errorMessage = 'Something went wrong. Please try again.';
        } finally {
            this.loading = false;
        }
    },

    async loadFilterOptions(filter) {
        if (!filter.requiresOptions) {
            this.filterOptions = [];
            this.groupedOptions = null;
            return;
        }

        this.loading = true;
        this.filterOptions = [];
        this.groupedOptions = null;
        this.hasMorePages = false;
        this.currentPage = 1;
        this.errorMessage = null;

        try {
            const response = await this.$wire.call('getFilterOptions', filter.value, 1, this.searchQuery);

            if (response.error) {
                this.errorMessage = response.error;
                this.filterOptions = [];
                this.groupedOptions = null;
                this.hasMorePages = false;
            } else {
                this.filterOptions = response.data || [];
                this.groupedOptions = response.grouped || null;
                this.hasMorePages = response.hasMorePages || false;
                this.currentPage = response.currentPage || 1;
            }
        } catch (error) {
            console.error('Failed to load filter options:', error);
            this.errorMessage = 'Unable to load filter options. Please try again.';
            this.filterOptions = [];
            this.groupedOptions = null;
            this.hasMorePages = false;
        } finally {
            this.loading = false;
        }
    },

    async loadMoreOptions() {
        if (!this.selectedFilter?.requiresOptions || !this.hasMorePages || this.loading) {
            return;
        }

        this.loading = true;

        try {
            const nextPage = this.currentPage + 1;
            const response = await this.$wire.call('getFilterOptions', this.selectedFilter.value, nextPage, this.searchQuery);

            if (response.error) {
                this.errorMessage = response.error;
                this.hasMorePages = false;
            } else {
                // Append new options to existing ones
                this.filterOptions = [...this.filterOptions, ...(response.data || [])];
                this.hasMorePages = response.hasMorePages || false;
                this.currentPage = response.currentPage || nextPage;
            }
        } catch (error) {
            console.error('Failed to load more filter options:', error);
            this.errorMessage = 'Unable to load more options. Please try again.';
            this.hasMorePages = false;
        } finally {
            this.loading = false;
        }
    },

    toggleValue(value) {
        // For single-select filter types, set the value and auto-apply
        if (['boolean', 'date_preset', 'date', 'year_period'].includes(this.selectedFilter.filterType)) {
            this.selectedValues = [value];
            this.autoApplyFilter();
            return;
        }

        // For multiselect filters, toggle the value in the array
        if (this.selectedFilter.filterType === 'multiselect') {
            const index = this.selectedValues.indexOf(value);
            if (index > -1) {
                this.selectedValues.splice(index, 1);
            } else {
                this.selectedValues.push(value);
            }
            this.debouncedAutoApply();
            return;
        }

        // For text and other types, set the value directly
        this.selectedValues = [value];
        this.debouncedAutoApply();
    },

    isValueSelected(value) {
        return this.selectedValues.includes(value);
    },

    async loadCurrentValues(filter) {
        try {
            this.selectedValues = await this.$wire.call('getCurrentFilterValues', filter.value) || [];

            if (filter.filterType === 'integer' && this.selectedValues.length > 0) {
                this.integerValue = this.selectedValues[0];
            }
        } catch (error) {
            console.warn('Could not load current filter values:', error);
            this.selectedValues = [];
        }
    },

    selectSingleValueAndApply(value) {
        // Add this value to selected values if not already selected
        if (!this.selectedValues.includes(value)) {
            this.selectedValues.push(value);
        }

        // Apply immediately and close menu
        this.autoApplyFilter(true);
    },

    autoApplyFilter(closeMenu = null) {
        if (!this.canApplyFilter) {
            return;
        }

        // Call updateFilter method on parent or directly on wire component
        const property = this.getFilterProperty(this.selectedFilter);
        const processedValue = this.processFilterValue(this.selectedFilter, this.selectedValues);

        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
        target.updateFilter(property, processedValue);

        // Close menu for single-select filters (boolean, date_preset, year_period) or when explicitly requested
        if (closeMenu === true || this.selectedFilter.filterType === 'boolean' || this.selectedFilter.filterType === 'date_preset' || this.selectedFilter.filterType === 'year_period') {
            this.closeFilterMenu();
        }
    },

    debouncedAutoApply() {
        clearTimeout(this.autoApplyTimeout);
        this.autoApplyTimeout = setTimeout(() => {
            if (this.selectedValues.length > 0) {
                this.autoApplyFilter();
            } else {
                // If no values selected, remove the filter
                const property = this.getFilterProperty(this.selectedFilter);
                this.$wire.updateFilter(property, null);
                this.closeFilterMenu();
            }
        }, 500); // 500ms debounce for multiselect
    },

    applyIntegerFilter() {
        if (!this.integerValue || this.integerValue < 1) {
            return;
        }

        this.selectedValues = [this.integerValue];
        const property = this.getFilterProperty(this.selectedFilter);
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
        target.updateFilter(property, this.integerValue);
        this.closeFilterMenu();
    },

    filterSearchResults(query) {
        if (!this.selectedFilter?.isSearchable || !this.selectedFilter?.requiresOptions) {
            return;
        }

        // Debounce search
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            try {
                const searchPropertyName = this.getSearchPropertyName(this.selectedFilter);
                this.$wire.set(searchPropertyName, query || '');

                // Wait a bit for Livewire to update, then reload options
                setTimeout(() => {
                    this.loadFilterOptions(this.selectedFilter);
                }, 100);
            } catch (error) {
                console.warn(`Could not search options for filter ${this.selectedFilter.name}:`, error);
            }
        }, 300);
    },

    getSearchPropertyName(filter) {
        return searchPropertyMap[filter.value] || null;
    },

    // Helper method to get filter property from filter config
    getFilterProperty(filterConfig) {
        return propertyMap[filterConfig.value] || filterConfig.value;
    },

    // Path to read a not-yet-applied filter value off the target component.
    // Dashboards stage into `pendingFilters`; a comparison editor is already
    // working on a staged copy, so it reads its own `filters`.
    stagedFilterProperty(property) {
        return this.useParent ? `pendingFilters.${property}` : `filters.${property}`;
    },

    // Helper method to process filter values for the parent component
    processFilterValue(filterConfig, values) {
        if (!values || values.length === 0) {
            return null;
        }

        // For multiselect filters, return the array of values
        if (filterConfig.filterType === 'multiselect') {
            return values;
        }

        // For integer filters, parse as integer
        if (filterConfig.filterType === 'integer') {
            return parseInt(values[0], 10);
        }

        // For boolean filters whose UI label inverts the stored value
        // (e.g. "Exclude Waves In Progress" → stored as includeWavesInProgress),
        // flip the boolean before sending it to the server.
        if (filterConfig.filterType === 'boolean' && filterConfig.invertsBooleanValue) {
            return !values[0];
        }

        // For score filters, return the array of score filter objects
        if (filterConfig.filterType === 'score_filter') {
            return values;
        }

        // For all other filter types (boolean, date_preset, date, text), return the first value
        return values[0] ?? null;
    }
});
